import axios from "axios";

const ACCESS_TOKEN_LIFETIME_MS = 30 * 60 * 1000; // 30 minutes
const REFRESH_TOKEN_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SESSION_AUTO_REFRESH_WINDOW_MS = 12 * 60 * 60 * 1000; // 12 hours
const SESSION_WARNING_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

// Determine the base URL for auth operations
const getAuthBaseUrl = () => {
  if (process.env.NODE_ENV === "production") {
    return "https://api.parkwithparallel.com";
  }

  // For development, dynamically determine the backend URL
  const currentHost = window.location.hostname;

  // If accessing from localhost, use localhost for backend
  if (currentHost === "localhost" || currentHost === "127.0.0.1") {
    return "http://localhost:8085/ParkingWithParallel";
  }

  // If accessing from IP address (like from phone), use the same IP for backend
  return `http://${currentHost}:8085/ParkingWithParallel`;
};

// Create a separate axios instance for auth operations to avoid circular imports
const authApi = axios.create({
  baseURL: getAuthBaseUrl(),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

interface AuthPayload {
  userId: string;
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
  refreshTokenExpiresAt: number;
  sessionAutoRefreshExpiresAt: number;
}

// Store user authentication data with both access and refresh tokens
export const storeAuthData = (
  userId: string,
  accessToken: string,
  refreshToken: string,
  sessionAutoRefreshExpiresAt?: number
) => {
  const now = Date.now();
  const accessTokenExpiresAt = now + ACCESS_TOKEN_LIFETIME_MS;
  const refreshTokenExpiresAt = now + REFRESH_TOKEN_LIFETIME_MS;
  const existingSessionExpiryStr = localStorage.getItem(
    "sessionAutoRefreshExpiresAt"
  );
  const existingSessionExpiry = existingSessionExpiryStr
    ? parseInt(existingSessionExpiryStr, 10)
    : null;
  const autoRefreshExpiresAt =
    sessionAutoRefreshExpiresAt ??
    existingSessionExpiry ??
    now + SESSION_AUTO_REFRESH_WINDOW_MS;

  const authData: AuthPayload = {
    userId,
    accessToken,
    refreshToken,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
    sessionAutoRefreshExpiresAt: autoRefreshExpiresAt,
  };

  // Clear any existing auth data first
  clearAuthData();

  // Store new auth data
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("userId", userId);
  localStorage.setItem("loggedInUserId", userId); // Store as loggedInUserId for consistent reference
  localStorage.setItem("accessTokenExpiresAt", accessTokenExpiresAt.toString());
  localStorage.setItem(
    "refreshTokenExpiresAt",
    refreshTokenExpiresAt.toString()
  );
  localStorage.setItem(
    "sessionAutoRefreshExpiresAt",
    autoRefreshExpiresAt.toString()
  );
  localStorage.setItem("isAuthenticated", "true");

  // Verify storage was successful
  const storedAccessToken = localStorage.getItem("accessToken");
  const storedRefreshToken = localStorage.getItem("refreshToken");
  const storedUserId = localStorage.getItem("loggedInUserId");

  if (
    storedAccessToken !== accessToken ||
    storedRefreshToken !== refreshToken ||
    storedUserId !== userId
  ) {
    return null;
  }

  return authData;
};

// Update only the access token (used after refresh)
export const updateAccessToken = (newAccessToken: string) => {
  const newAccessTokenExpiresAt = Date.now() + ACCESS_TOKEN_LIFETIME_MS; // 30 minutes from now

  localStorage.setItem("accessToken", newAccessToken);
  localStorage.setItem(
    "accessTokenExpiresAt",
    newAccessTokenExpiresAt.toString()
  );
};

// Validate that authentication data is properly stored
export const validateAuthStorage = (): boolean => {
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");
  const userId = localStorage.getItem("userId");
  const accessTokenExpiresAtStr = localStorage.getItem("accessTokenExpiresAt");
  const refreshTokenExpiresAtStr = localStorage.getItem(
    "refreshTokenExpiresAt"
  );
  const sessionAutoRefreshExpiresAtStr = localStorage.getItem(
    "sessionAutoRefreshExpiresAt"
  );
  const isAuthStr = localStorage.getItem("isAuthenticated");

  return !!(
    accessToken &&
    refreshToken &&
    userId &&
    accessTokenExpiresAtStr &&
    refreshTokenExpiresAtStr &&
    sessionAutoRefreshExpiresAtStr &&
    isAuthStr === "true"
  );
};

// Retrieve and validate authentication data
export const getAuthData = (): AuthPayload | null => {
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");
  const userId = localStorage.getItem("userId");
  const accessTokenExpiresAtStr = localStorage.getItem("accessTokenExpiresAt");
  const refreshTokenExpiresAtStr = localStorage.getItem(
    "refreshTokenExpiresAt"
  );
  const sessionAutoRefreshExpiresAtStr = localStorage.getItem(
    "sessionAutoRefreshExpiresAt"
  );

  if (
    !accessToken ||
    !refreshToken ||
    !userId ||
    !accessTokenExpiresAtStr ||
    !refreshTokenExpiresAtStr
  ) {
    return null;
  }

  const accessTokenExpiresAt = parseInt(accessTokenExpiresAtStr, 10);
  const refreshTokenExpiresAt = parseInt(refreshTokenExpiresAtStr, 10);
  const sessionAutoRefreshExpiresAt = sessionAutoRefreshExpiresAtStr
    ? parseInt(sessionAutoRefreshExpiresAtStr, 10)
    : Date.now() + SESSION_AUTO_REFRESH_WINDOW_MS;

  // Backfill missing session window for legacy sessions
  if (!sessionAutoRefreshExpiresAtStr) {
    localStorage.setItem(
      "sessionAutoRefreshExpiresAt",
      sessionAutoRefreshExpiresAt.toString()
    );
  }

  // Check if refresh token has expired
  if (Date.now() > refreshTokenExpiresAt) {
    clearAuthData();
    return null;
  }

  return {
    accessToken,
    refreshToken,
    userId,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
    sessionAutoRefreshExpiresAt,
  };
};

// Get access token
export const getAccessToken = (): string | null => {
  const authData = getAuthData();
  return authData?.accessToken || null;
};

export const getCurrentUserRoleFromToken = (): string | null => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1]));
    const role = payload?.role || payload?.Role || payload?.userRole;
    return role ? role.toString().toLowerCase() : null;
  } catch {
    return null;
  }
};

// Get refresh token
export const getRefreshToken = (): string | null => {
  const authData = getAuthData();
  return authData?.refreshToken || null;
};

// Check if user is authenticated (has valid refresh token)
export const isAuthenticated = (): boolean => {
  const authData = getAuthData();
  return authData !== null;
};

// Check if access token is expired
export const isAccessTokenExpired = (): boolean => {
  const authData = getAuthData();
  if (!authData) return true;

  return Date.now() > authData.accessTokenExpiresAt;
};

// Check if session is about to expire (2 minutes before expiration)
export const isSessionAboutToExpire = (): boolean => {
  const authData = getAuthData();
  if (!authData) return false;

  const warningTime = Date.now() + SESSION_WARNING_THRESHOLD_MS;
  return warningTime > authData.sessionAutoRefreshExpiresAt;
};

// Check if session has exceeded the 12 hour automatic refresh window
export const isAutoRefreshWindowExpired = (): boolean => {
  const authData = getAuthData();
  if (!authData) return true;

  return Date.now() > authData.sessionAutoRefreshExpiresAt;
};

// Get time until the 12-hour auto-refresh window expires (in milliseconds)
export const getTimeUntilExpiry = (): number => {
  const authData = getAuthData();
  if (!authData) return 0;

  const timeUntilExpiry = authData.sessionAutoRefreshExpiresAt - Date.now();
  return Math.max(0, timeUntilExpiry);
};

export const clearAuthData = () => {
  // Preserve non-sensitive UI preferences when clearing auth data
  const preservedKeys = ["themeMode", "ai_chat_width"];
  const preservedEntries = preservedKeys.map((key) => [key, localStorage.getItem(key)] as const);

  // Clear all localStorage to ensure no user data persists
  localStorage.clear();

  // Restore preserved preferences (theme, chat width) if they existed
  preservedEntries.forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });
};

// Get the stored token (for backward compatibility)
export const getToken = (): string | null => {
  return getAccessToken();
};

// Refresh the access token using the refresh token
interface RefreshOptions {
  extendSession?: boolean;
}

export const refreshAccessToken = async (
  options: RefreshOptions = {}
): Promise<boolean> => {
  try {
    const authData = getAuthData();
    const refreshToken = authData?.refreshToken;
    if (!refreshToken || !authData) {
      return false;
    }

    const sessionExpired = isAutoRefreshWindowExpired();
    if (sessionExpired && !options.extendSession) {
      return false;
    }

    // Call the refresh endpoint with refresh token in Authorization header
    const response = await authApi.post(
      "/login/refresh",
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );

    // Extract new tokens from response
    const {
      accessToken,
      refreshToken: newRefreshToken,
      userId,
    } = response.data;

    if (!accessToken || !newRefreshToken || !userId) {
      return false;
    }

    // Store the new tokens
    const newAuthData = storeAuthData(
      userId,
      accessToken,
      newRefreshToken,
      options.extendSession
        ? Date.now() + SESSION_AUTO_REFRESH_WINDOW_MS
        : authData.sessionAutoRefreshExpiresAt
    );

    return !!newAuthData;
  } catch {

    // If refresh fails, clear auth data and redirect to login
    clearAuthData();

    // Only redirect if we're not already on the login page
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }

    return false;
  }
};

// Extend the current session (legacy function for backward compatibility)
export const extendSession = async (): Promise<boolean> => {
  return await refreshAccessToken({ extendSession: true });
};

// Development utility function to test session expiration
export const simulateTokenExpiry = (minutesFromNow: number = 0): void => {
  if (process.env.NODE_ENV !== "development") {
    return;
  }

  const newExpiryTime = Date.now() + minutesFromNow * 60 * 1000;
  localStorage.setItem("accessTokenExpiresAt", newExpiryTime.toString());
};

// Add to window for easy testing in development
if (process.env.NODE_ENV === "development") {
  (window as any).testSessionExpiry = {
    simulateExpiry: simulateTokenExpiry,
    showWarning: () => simulateTokenExpiry(1.5), // Show warning in 1.5 minutes
    expireNow: () => simulateTokenExpiry(-1), // Expire immediately
    checkStatus: () => ({
      isAuthenticated: isAuthenticated(),
      isExpired: isAccessTokenExpired(),
      isAboutToExpire: isSessionAboutToExpire(),
      timeUntilExpiry: getTimeUntilExpiry(),
    }),
  };
}
